/**
 * Component  for compact deal card component
 *
 * @module views/components/CompactDealCard
 * @memberof -Common
 */
import './CompactDealCard.scss';

import React, { useEffect, useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Image from '@ulta/core/components/Image/Image';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';

import { constants } from '@ulta/utils/constants/constants';

import * as utils from './CompactDealCard';

const { COMAPCTDEALCARD_IMAGE_DIMENSIONS } = constants;

/**
 * Represents a CompactDealCard component
 *
 * @method
 * @param {CompactDealCardProps} props - React properties passed from composition
 * @returns CompactDealCard
 */


export const CompactDealCard = function( props ){
  const { breakpoint } = useDeviceInflection();
  const isSmallDevice = breakpoint.isSmallDevice();
  const { images, eyebrow, tag, itemClickAction, headline, subHeadline, isGrid, promotionId, onIntersectionEvent, root, rootMargin } = props;
  const compactDealRef = useRef();
  const { intersectionRatio, hasIntersected } = useIntersectionObserver( compactDealRef, {
    root: root,
    rootMargin: rootMargin,
    threshold: [0, 0.5, 1.0]
  } );
  useEffect( () => {
    if( intersectionRatio === 1 ){
      utils.onIntersection( { promotionId }, { onIntersectionEvent } );
    }
  }, [hasIntersected, intersectionRatio, onIntersectionEvent] );
  const hasImages = Array.isArray( images ) && images?.length > 0;
  if( !hasImages && !tag && !eyebrow && !headline && !subHeadline && !itemClickAction ){
    return null;
  }
  const imgDimension = setImageParams( { lengthOfImages: ( hasImages && images.length ), breakpoint: breakpoint } ) * ( global.devicePixelRatio || 1 );

  const CardDetails = () => {
    return (
      <>
        <div className='CompactDealCard__gwpLine'>
          { tag && isSmallDevice && !isGrid &&
            <span className='CompactDealCard__tag'>
              <CardTag
                messageText={ tag }
                textStyle='body-3'
              />
            </span>
          }
          { eyebrow &&
            <div className='CompactDealCard__eyebrow'>
              <CardTag
                messageText={ eyebrow }
                textStyle='body-3'
                color='neutral-600'
              />
            </div>
          }
          { headline &&
            <div className='CompactDealCard__headline'>
              <CardTag
                messageText={ headline }
                textStyle='title-6'
              />
            </div>
          }
          { subHeadline &&
            <div className='CompactDealCard__subHeadline'>
              <CardTag
                messageText={ subHeadline }
                textStyle='body-2'
              />
            </div>
          }
        </div>
      </>
    );
  };
  return (
    <>
      { !isGrid && (
        // This is for GWP
        <div
          className='CompactDealCard CompactDealCard__displayMobile'
          ref={ compactDealRef }
          data-visible={ intersectionRatio === 1 }
          data-item-id={ props.promotionId }
        >
          <Link_Huge
            className='CompactDealCard__listDisplay'
            action={ itemClickAction }
          >
            { !isSmallDevice && tag &&
              <CardTag
                messageText={ tag }
                textStyle='body-3'
              />
            }
            <div className='CompactDealCard__flexItemRight'>
              <div
                className={
                  classNames( 'CompactDealCard__image', {
                    'CompactDealCard__multipleImages': ( hasImages && images.length === 2 ),
                    'CompactDealCard__singleImage': ( hasImages && images.length === 1 )
                  } )
                }
              >
                { hasImages && images.map( ( item, index ) => (
                  <Image
                    key={ index }
                    alt=''
                    src={ item.imageUrl }
                    metaData={ {
                      width: imgDimension,
                      height: imgDimension,
                      imageTemplate: 'ProductCardNeutralBG'
                    } }
                  />
                ) ) }
              </div>
            </div>
            <CardDetails />
          </Link_Huge>
        </div>
      ) }
      { isGrid && (
        // This is for BMSM
        <div className='CompactDealCard'
          ref={ compactDealRef }
          data-visible={ intersectionRatio === 1 }
          data-item-id={ props.promotionId }
        >
          <Link_Huge
            action={ itemClickAction }
          >
            { tag &&
              <span className='CompactDealCard__tag'>
                <CardTag
                  messageText={ tag }
                  textStyle='body-3'
                />
              </span>
            }
            <div
              className={
                classNames( 'CompactDealCard__image', {
                  'CompactDealCard__multipleImages': ( hasImages && images.length === 2 )
                } )
              }
            >
              { hasImages && images.map( ( item, index ) => (
                <Image
                  key={ index }
                  alt=''
                  src={ item.imageUrl }
                  metaData={ {
                    width: imgDimension,
                    height: imgDimension,
                    imageTemplate: 'ProductCardNeutralBG'
                  } }
                />
              ) ) }
            </div>
            <CardDetails />
          </Link_Huge>
        </div>
      ) }
    </>
  );
};

/**
 * Function to return image dimension.
 *
 * @method imageDimension
 * @param {object} data - This is object of breakpoint, size for Compact Deal Card for all devices, i.e small-medium/small-list/large-xLarge.
 * @returns {number} Returns number consisting width and height
 **/

export const imageDimension = ( data ) => {
  const { breakpoint, size } = data;

  switch ( breakpoint.CURRENT_BREAKPOINT ){
    case breakpoint.BREAKPOINTS[0]:
      return size[0];
    case breakpoint.BREAKPOINTS[1]:
      return size[1];
    case breakpoint.BREAKPOINTS[2]:
      return size[2];
    case breakpoint.BREAKPOINTS[3]:
      return size[3];
    default:
      return size[3]; // default for XL/LG
  }
};

/**
 * Function to return image params.
 *
 * @method setImageParams
 * @param {object} data - This is object of lengthOfImages, breakpoint for Compact Deal Card
 * @returns {number} Returns number consisting width and height
 **/

export const setImageParams = ( data ) => {
  const { lengthOfImages, breakpoint } = data;
  if( lengthOfImages === 1 ){
    return imageDimension( { breakpoint, size: COMAPCTDEALCARD_IMAGE_DIMENSIONS.singleImage } );
  }
  else if( lengthOfImages === 2 ){
    return imageDimension( { breakpoint, size: COMAPCTDEALCARD_IMAGE_DIMENSIONS.multiImage } );
  }
};

/**
 * Represents a CardTag component
 *
 * @method
 * @param {object} props - React properties passed from composition
 * @returns CardTag
 */
export const CardTag = function( { messageText, textStyle, color } ){
  return (
    <div className='CompactDealCard__flexItemLeft'>
      <Text
        textStyle={ textStyle }
        color={ color }
      >
        { messageText }
      </Text>
    </div>
  );
};

/**
 * Method to handle on onIntersection event
 * @param {object} data
 * @param {function} onIntersectionEvent
 */
export const onIntersection = ( data, methods ) => {
  const { promotionId } = data || {};
  const { onIntersectionEvent } = methods || {};
  if( promotionId && onIntersectionEvent ){
    onIntersectionEvent( { promotionId } );
  }
};

/**
 * Property type definitions
 * @typedef CompactDealCardProps
 * @type {object}
 * @property {object} itemClickAction - This is the itemClickAction for the Compact Deal Card
 * @property {string} headline - This is the headline for the Compact Deal Card
 * @property {string} subHeadline - This is the subHeadline for the Compact Deal Card
 * @property {string} tag - This is the tag for the Compact Deal Card
 * @property {string} eyebrow - This is the eyebrow for the Compact Deal Card
 * @property {array} images - This is the array of images for the Compact Deal Card
 * @property {boolean} isGrid - This is isGrid boolean value used to display list layout
*/
export const propTypes = {
  itemClickAction: PropTypes.shape( {
    url: PropTypes.string
  } ),
  headline: PropTypes.string,
  subHeadline: PropTypes.string,
  tag: PropTypes.string,
  eyebrow: PropTypes.string,
  images: PropTypes.array,
  isGrid: PropTypes.bool
};

CompactDealCard.propTypes = propTypes;

export default CompactDealCard;